<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useSettingsStore } from '../stores/settingsStore'

const settingsStore = useSettingsStore()

const route = useRoute()
const articles = ref([])
const categories = ref([])
const loading = ref(true)
const currentPage = ref(1)
const totalPages = ref(1)
const totalArticles = ref(0)
const selectedCategory = ref('all')
const searchQuery = ref('')

const filteredArticles = computed(() => {
  if (!searchQuery.value) return articles.value
  const query = searchQuery.value.toLowerCase().trim()
  return articles.value.filter(article => 
    article.title?.toLowerCase().includes(query) || 
    article.excerpt?.toLowerCase().includes(query) ||
    article.category?.toLowerCase().includes(query)
  )
})

const getReadingTime = (article) => {
  const textLength = (article.title || '').length + (article.excerpt || '').length + (article.content || '').length;
  const time = Math.ceil(textLength / 250); 
  return time < 2 ? 2 : time;
}

const displayArticles = computed(() => {
  if (searchQuery.value) return filteredArticles.value
  if (currentPage.value === 1 && selectedCategory.value === 'all' && articles.value.length > 0) {
    return articles.value.slice(1)
  }
  return articles.value
})

const loadArticles = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams({ page: currentPage.value, limit: 12 })
    if (selectedCategory.value !== 'all') params.append('category', selectedCategory.value)
    
    const res = await fetch(`/api/articles/published?${params}`)
    const data = await res.json()
    if (data.success) {
      articles.value = data.data
      totalPages.value = data.pagination.totalPages
      totalArticles.value = data.pagination.total
    }
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const paginationRange = computed(() => {
    const range = []
    const total = totalPages.value
    const current = currentPage.value
    const delta = 2
    let left = Math.max(2, current - delta)
    let right = Math.min(total - 1, current + delta)
    
    range.push(1)
    if (left > 2) range.push('...')
    for (let i = left; i <= right; i++) range.push(i)
    if (right < total - 1) range.push('...')
    if (total > 1) range.push(total)
    return range
})

const loadCategories = async () => {
  try {
    const res = await fetch('/api/articles/categories')
    const data = await res.json()
    if (data.success) categories.value = data.data
  } catch (e) { /* */ }
}

const filterCategory = (cat) => {
  selectedCategory.value = cat
  currentPage.value = 1
  loadArticles()
}

const changePage = (p) => {
  currentPage.value = p
  loadArticles()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const formatArticleDate = (dateString) => {
  if (!dateString) return 'ล่าสุด';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return 'ล่าสุด';
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch(e) {
    return 'ล่าสุด';
  }
}

// Schema
const addSchema = () => {
  const baseUrl = settingsStore.storeUrl || window.location.origin
  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": settingsStore.storeName ? `บทความ ${settingsStore.storeName}` : 'บทความทั้งหมด',
    "description": settingsStore.storeDescription || 'บทความให้ความรู้และการดูแลรักษา',
    "url": `${baseUrl}/blog`,
    "publisher": {
      "@type": "Organization",
      "name": settingsStore.storeName || '',
      "url": baseUrl
    }
  }
  const s = document.createElement('script')
  s.type = 'application/ld+json'
  s.text = JSON.stringify(schema)
  s.id = 'json-ld-blog'
  document.head.appendChild(s)
}

onMounted(() => {
  loadArticles()
  loadCategories()
  addSchema()
})

onUnmounted(() => {
  const s = document.getElementById('json-ld-blog')
  if (s) document.head.removeChild(s)
})
</script>

<template>
  <div class="bg-[#f8f9fa] dark:bg-[#0a0f16] min-h-screen pt-24 md:pt-32 pb-20 transition-colors relative overflow-hidden">
    <!-- Ambient Decor (Animated Background) -->
    <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCI+CgkJPGcgc3Ryb2tlPSIjMTA4YjY1IiBzdHJva2Utd2lkdGg9IjEiIG9wYWNpdHk9IjAuMDUiPgoJCQk8cGF0aCBkPSJNMCAwaDQwdjQwaC00MHoiIGZpbGw9Im5vbmUiLz4KCQk8L2c+Cjwvc3ZnPg==')] [mask-image:linear-gradient(to_bottom,white_10%,transparent_80%)] dark:[mask-image:linear-gradient(to_bottom,rgba(255,255,255,0.05)_10%,transparent_80%)] z-0"></div>
    <div class="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
      <div class="absolute -top-[10%] right-[0%] w-[60vw] max-w-[600px] h-[60vw] max-h-[600px] bg-emerald-500/10 dark:bg-emerald-500/5 blur-[120px] rounded-full animate-[spin_20s_linear_infinite] origin-center mix-blend-multiply dark:mix-blend-lighten"></div>
      <div class="absolute top-[20%] -left-[10%] w-[50vw] max-w-[500px] h-[50vw] max-h-[500px] bg-teal-500/10 dark:bg-teal-500/5 blur-[100px] rounded-full animate-[pulse_10s_ease-in-out_infinite] mix-blend-multiply dark:mix-blend-lighten"></div>
      <div class="absolute bottom-[10%] right-[10%] w-[40vw] max-w-[400px] h-[40vw] max-h-[400px] bg-indigo-500/5 dark:bg-indigo-500/5 blur-[90px] rounded-full animate-[pulse_12s_ease-in-out_infinite]"></div>
    </div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      
      <!-- Hero Header -->
      <div class="text-center mb-16 md:mb-20 relative mt-8 md:mt-12">
        <div class="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/60 dark:bg-white/5 border border-gray-200/80 dark:border-white/10 backdrop-blur-md mb-8 shadow-sm">
          <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
          <span class="text-xs font-bold text-gray-600 dark:text-gray-300 tracking-widest uppercase">Blog & Knowledge</span>
        </div>
        <h1 class="text-5xl md:text-7xl lg:text-[5.5rem] font-black text-gray-900 dark:text-white leading-[1.1] tracking-tight mb-8">
          บทความและ<br class="md:hidden" />
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400">ความรู้</span>
        </h1>
        <p class="text-lg md:text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto font-medium leading-relaxed">
          เจาะลึกเทคนิคการจัดพื้นที่ เคล็ดลับดูแลรักษา และแรงบันดาลใจในการตกแต่งบ้านเก็บของและโรงเรือน
        </p>

        <!-- Search Input -->
        <div class="mt-12 max-w-lg mx-auto relative group">
          <div class="absolute -inset-0.5 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-500"></div>
          <div class="relative flex items-center">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="ค้นหาบทความด้วยคีย์เวิร์ด..." 
              class="w-full px-6 py-4 rounded-full bg-white dark:bg-gray-950 border border-gray-200 dark:border-white/5 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent backdrop-blur-md shadow-lg text-sm md:text-base transition-all duration-300"
            />
            <span class="absolute right-5 text-gray-400 dark:text-gray-500 group-hover:text-emerald-500 transition-colors">
              <svg v-if="!searchQuery" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
              <button v-else @click="searchQuery = ''" class="focus:outline-none hover:text-red-500 transition-colors">
                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </span>
          </div>
        </div>
      </div>

      <!-- Category Filter -->
      <div class="flex overflow-x-auto gap-3 mb-16 pb-6 scrollbar-hide justify-start lg:justify-center items-center px-4 -mx-4 sm:px-0 sm:mx-0" v-if="categories.length > 0">
        <div class="flex gap-2 p-1.5 bg-white/60 dark:bg-gray-950/40 backdrop-blur-md border border-gray-200/80 dark:border-white/5 rounded-full shadow-sm mx-auto">
          <button @click="filterCategory('all')" 
                  :class="selectedCategory === 'all' ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/20 scale-102' : 'bg-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'" 
                  class="px-6 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-300 flex items-center gap-2 shrink-0 hover:-translate-y-0.5">
            ทั้งหมด
          </button>
          <button v-for="cat in categories" :key="cat.category" @click="filterCategory(cat.category)" 
                  :class="selectedCategory === cat.category ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/20 scale-102' : 'bg-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'" 
                  class="px-6 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-300 flex items-center gap-3 shrink-0 group hover:-translate-y-0.5">
            {{ cat.category }} 
            <span :class="selectedCategory === cat.category ? 'bg-white/20 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-400 group-hover:bg-gray-200 dark:group-hover:bg-gray-700'" class="px-2.5 py-0.5 rounded-full text-[11px] transition-colors duration-300">{{ cat.count }}</span>
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div v-for="i in 6" :key="'skeleton-' + i" class="bg-white dark:bg-[#1a2333] rounded-[2rem] overflow-hidden border border-gray-100 dark:border-white/5 flex flex-col h-full animate-pulse shadow-sm">
          <div class="aspect-[16/10] bg-gray-200 dark:bg-gray-800"></div>
          <div class="p-8 flex flex-col flex-grow">
            <div class="h-6 bg-gray-200 dark:bg-gray-800 rounded-lg w-3/4 mb-4"></div>
            <div class="h-4 bg-gray-200 dark:bg-gray-800 rounded-lg w-full mb-3"></div>
            <div class="h-4 bg-gray-200 dark:bg-gray-800 rounded-lg w-2/3 mb-8 flex-grow"></div>
            <div class="flex items-center justify-between pt-5 border-t border-gray-50 dark:border-gray-800/50">
              <div class="h-4 bg-gray-200 dark:bg-gray-800 rounded-lg w-24"></div>
              <div class="h-8 w-8 bg-gray-200 dark:bg-gray-800 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Spotlight Banner (Only on page 1, category 'all', and when no active search) -->
      <div v-else-if="currentPage === 1 && selectedCategory === 'all' && !searchQuery && articles.length > 0" class="mb-16">
        <router-link 
          :to="'/blog/' + (articles[0].slug || articles[0].id)"
          class="group flex flex-col lg:flex-row bg-white/70 dark:bg-gray-900/40 backdrop-blur-xl rounded-[2.5rem] overflow-hidden shadow-[0_12px_40px_rgb(0,0,0,0.03)] dark:shadow-[0_12px_40px_rgb(0,0,0,0.3)] hover:shadow-[0_12px_45px_rgba(16,185,129,0.12)] border border-gray-200/80 dark:border-white/5 hover:border-emerald-500/30 transition-all duration-500"
        >
          <!-- Image Section -->
          <div class="lg:w-[55%] aspect-[16/10] lg:aspect-auto lg:min-h-[420px] overflow-hidden relative bg-gray-100 dark:bg-gray-800">
            <img v-if="articles[0].cover_image" :src="articles[0].cover_image" :alt="articles[0].title" class="w-full h-full object-cover transform group-hover:scale-102 transition-transform duration-[2s] ease-out">
            <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/10 dark:to-teal-900/10">
              <svg class="w-16 h-16 text-emerald-350 dark:text-emerald-800/30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7"/></svg>
            </div>
            <div class="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-black/60 via-black/10 to-transparent"></div>
            <!-- Spotlight Badges -->
            <div class="absolute top-6 left-6 flex flex-col gap-2 z-10">
              <span class="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-1.5 text-xs font-black rounded-full uppercase tracking-wider shadow-lg shadow-emerald-500/20">
                บทความล่าสุด
              </span>
              <span v-if="articles[0].is_featured" class="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 py-1.5 text-xs font-black rounded-full uppercase tracking-wider shadow-lg shadow-amber-500/20 flex items-center gap-1.5">
                <svg class="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                แนะนำ
              </span>
            </div>
          </div>
          <!-- Info Section -->
          <div class="lg:w-[45%] p-8 lg:p-12 flex flex-col justify-between">
            <div>
              <div class="flex items-center gap-3 mb-6 text-sm">
                <span class="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest bg-emerald-50 dark:bg-emerald-950/40 px-3.5 py-1.5 rounded-xl border border-emerald-100 dark:border-emerald-900/30">
                  {{ articles[0].category }}
                </span>
                <span class="text-gray-350 dark:text-gray-700">•</span>
                <span class="text-gray-500 dark:text-gray-400 font-medium">{{ formatArticleDate(articles[0].published_at || articles[0].created_at) }}</span>
                <span class="text-gray-350 dark:text-gray-700">•</span>
                <span class="text-gray-550 dark:text-gray-450 font-medium">อ่าน {{ getReadingTime(articles[0]) }} นาที</span>
              </div>
              <h2 class="text-3xl lg:text-4xl font-black text-gray-900 dark:text-white mb-6 line-clamp-3 leading-tight group-hover:text-emerald-500 transition-colors">
                {{ articles[0].title }}
              </h2>
              <p class="text-base text-gray-500 dark:text-gray-400 font-light line-clamp-4 leading-relaxed mb-8">
                {{ articles[0].excerpt || articles[0].seo_description || 'อ่านรายละเอียดเชิงลึกและเทคนิคต่างๆ เพิ่มเติมได้ภายในบทความนี้...' }}
              </p>
            </div>
            <div class="pt-6 border-t border-gray-100 dark:border-white/5 flex items-center justify-between">
              <span class="flex items-center gap-2 text-gray-400 text-sm font-medium">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                {{ articles[0].view_count || 0 }} เข้าชม
              </span>
              <div class="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 font-bold group-hover:gap-4 transition-all">
                <span>อ่านบทความนี้</span>
                <div class="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center group-hover:bg-emerald-500 group-hover:text-white transition-all shadow-sm">
                  <svg class="w-5 h-5 -rotate-45 group-hover:rotate-0 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                </div>
              </div>
            </div>
          </div>
        </router-link>
      </div>

      <!-- Empty Category / Search -->
      <div v-if="displayArticles.length === 0" class="text-center py-24 bg-white/60 dark:bg-gray-900/30 backdrop-blur-md rounded-[2.5rem] border border-gray-200/80 dark:border-white/5 shadow-sm">
        <div class="w-20 h-20 bg-gray-150 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg class="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"/></svg>
        </div>
        <p class="text-gray-900 dark:text-white font-black text-2xl mb-2">ไม่พบรายการบทความ</p>
        <p class="text-gray-500 dark:text-gray-450">ไม่พบบทความใดๆ ที่ตรงกับเงื่อนไขการเลือกของคุณในขณะนี้</p>
        <button v-if="searchQuery" @click="searchQuery = ''" class="mt-8 px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full font-bold shadow-lg shadow-emerald-500/20 transition-all inline-flex items-center gap-2">
          ล้างค่าการค้นหา
        </button>
        <button v-else-if="selectedCategory !== 'all'" @click="filterCategory('all')" class="mt-8 px-6 py-2.5 bg-emerald-50 dark:bg-emerald-950/20 text-emerald-600 dark:text-emerald-400 rounded-full font-bold hover:bg-emerald-100 transition-colors inline-flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg> กลับไปหน้าทั้งหมด
        </button>
      </div>

      <!-- Article Grid -->
      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
        <router-link 
          v-for="article in displayArticles" :key="article.id"
          :to="'/blog/' + (article.slug || article.id)"
          class="group flex flex-col bg-white/60 dark:bg-gray-900/30 backdrop-blur-md rounded-[2rem] overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.02)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:shadow-[0_12px_40px_rgba(16,185,129,0.1)] transition-all duration-500 border border-gray-200/80 dark:border-white/5 hover:-translate-y-2 hover:border-emerald-350/50 dark:hover:border-emerald-500/30"
        >
          <!-- Cover Image -->
          <div class="aspect-[16/10] overflow-hidden bg-gray-100 dark:bg-gray-800 relative">
            <img v-if="article.cover_image" :src="article.cover_image" :alt="article.title" class="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-[1.5s] ease-out">
            <div v-else class="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/10 dark:to-teal-900/10">
              <svg class="w-12 h-12 text-emerald-350 dark:text-emerald-800/40" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7"/></svg>
            </div>
            <div class="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-black/0 opacity-40 group-hover:opacity-75 transition-opacity duration-500"></div>

            <!-- Floating Badges -->
            <div class="absolute top-5 left-5 flex flex-col gap-2 z-10">
              <div v-if="article.is_featured" class="bg-gradient-to-r from-amber-400 to-orange-500 text-white px-3 py-1.5 text-[10px] font-black rounded-full uppercase tracking-wider shadow-sm flex items-center gap-1 w-fit">
                <svg class="w-3 h-3" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>
                แนะนำ
              </div>
              <div class="bg-white/95 dark:bg-gray-900/95 backdrop-blur-md px-3.5 py-1.5 rounded-xl shadow-sm border border-gray-150 dark:border-white/10 w-fit">
                <p class="text-[10px] font-bold tracking-wider text-emerald-600 dark:text-emerald-400 uppercase flex items-center gap-1.5">
                  <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  {{ formatArticleDate(article.published_at || article.created_at) }}
                </p>
              </div>
            </div>

            <!-- Read Time Floating Badge -->
            <div class="absolute top-5 right-5 z-10 bg-black/50 backdrop-blur-md text-white/90 px-3 py-1.5 rounded-xl text-[10px] font-bold tracking-wider border border-white/10">
              อ่าน {{ getReadingTime(article) }} นาที
            </div>

            <!-- Read more button floating -->
            <div class="absolute bottom-5 right-5 translate-y-8 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out delay-100 z-10">
              <div class="w-12 h-12 bg-emerald-500 text-white rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/40">
                <svg class="w-5 h-5 -rotate-45 group-hover:rotate-0 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path></svg>
              </div>
            </div>
          </div>

          <!-- Content -->
          <div class="p-6 md:p-8 flex-grow flex flex-col justify-between relative">
            <div>
              <div class="mb-3">
                <span class="text-[10px] font-black text-emerald-500 uppercase tracking-widest bg-emerald-500/10 px-2.5 py-1 rounded-lg">{{ article.category }}</span>
              </div>
              <h4 class="font-bold text-gray-900 dark:text-white text-xl md:text-2xl mb-3 line-clamp-2 leading-snug group-hover:text-emerald-500 transition-colors">{{ article.title }}</h4>
              <p class="text-sm text-gray-500 dark:text-gray-450 font-light line-clamp-3 leading-relaxed mb-6 flex-grow">{{ article.excerpt || article.seo_description || 'อ่านรายละเอียดเชิงลึกและเทคนิคต่างๆ เพิ่มเติมได้ภายในบทความนี้...' }}</p>
            </div>
            <div class="mt-auto pt-5 border-t border-gray-100 dark:border-white/5 flex items-center justify-between text-sm font-bold text-gray-900 dark:text-white group-hover:text-emerald-500 transition-colors">
              <span class="flex items-center gap-1.5 text-gray-400 font-medium text-xs">
                <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                {{ article.view_count || 0 }} ยอดเข้าชม
              </span>
              <div class="w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center group-hover:bg-emerald-500 group-hover:text-white transition-all duration-300">
                <svg class="w-4 h-4 transform group-hover:translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
              </div>
            </div>
          </div>
        </router-link>
      </div>

      <!-- Pagination -->
      <div v-if="totalPages > 1 && !searchQuery" class="flex flex-col items-center gap-4 mt-16 pb-8">
        <div class="flex gap-2 items-center bg-white/80 dark:bg-gray-950/40 backdrop-blur-md p-1.5 rounded-full shadow-sm border border-gray-200/80 dark:border-white/5">
          <button @click="changePage(currentPage - 1)" :disabled="currentPage <= 1" :class="currentPage <= 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-emerald-50 dark:hover:bg-emerald-950/20 text-gray-600 dark:text-gray-300 hover:text-emerald-500 dark:hover:text-emerald-400'" class="w-10 h-10 rounded-full text-sm font-bold transition-colors flex items-center justify-center">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7"/></svg>
          </button>
          
          <template v-for="p in paginationRange" :key="p">
            <span v-if="p === '...'" class="px-2 text-gray-400 font-bold tracking-widest">…</span>
            <button v-else @click="changePage(p)" 
                    :class="currentPage === p ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-md shadow-emerald-500/25' : 'bg-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'" 
                    class="w-10 h-10 rounded-full text-sm font-bold transition-all duration-350 flex items-center justify-center">
              {{ p }}
            </button>
          </template>
          
          <button @click="changePage(currentPage + 1)" :disabled="currentPage >= totalPages" :class="currentPage >= totalPages ? 'opacity-30 cursor-not-allowed' : 'hover:bg-emerald-50 dark:hover:bg-emerald-950/20 text-gray-600 dark:text-gray-300 hover:text-emerald-500 dark:hover:text-emerald-400'" class="w-10 h-10 rounded-full text-sm font-bold transition-colors flex items-center justify-center">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7"/></svg>
          </button>
        </div>
        <p class="text-xs font-semibold text-gray-450 dark:text-gray-500 tracking-wider">หน้า {{ currentPage }} จาก {{ totalPages }} <span class="mx-2 text-gray-300 dark:text-gray-700">•</span> มีทั้งหมด {{ totalArticles }} บทความ</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.scrollbar-hide::-webkit-scrollbar { display: none; }
.scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
</style>
